/**
 * Owned anonymous web identifier (`web_id`).
 *
 * A random, self-generated ID that we mint and persist in localStorage. This is the
 * canonical anonymous device identifier for the whole funnel: it is fed to Amplitude
 * (as `device_id`), pushed into Statsig (as `customIDs.stableID`), and sent to the
 * backend in checkout metadata as `web_id`.
 *
 * We own it — it no longer depends on Statsig's internally-generated stableID, so it
 * survives even if Statsig is reconfigured or removed. Generated lazily on first use;
 * the first call happens inside the consent-gated init path, keeping it consistent
 * with the app's analytics-consent posture (no pseudonymous id before consent).
 */

const WEB_ID_KEY = 'lunai_web_id';

/**
 * Return the stable web_id, creating and persisting one on first use.
 */
export function getWebId(): string {
  let id: string | null = null;

  try {
    id = localStorage.getItem(WEB_ID_KEY);
  } catch {
    // localStorage unavailable (e.g. private mode) — fall through to a volatile id.
  }

  if (!id) {
    id = generateWebId();
    try {
      localStorage.setItem(WEB_ID_KEY, id);
    } catch {
      // Persistence failed; the id is still valid for this page load.
    }
  }

  return id;
}

function generateWebId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return `web_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}
